import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { healthcareTeamManager } from "@core/service-container";
import { sendAnalyticsEventOpenInvitation } from "@domain/healthcare-team/healthcare-team-analytics";
import { InvitationAlreadyAcceptedError } from "@domain/healthcare-team/healthcare-team-errors";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { ToastContext } from "@ui/components/common/toast-provider";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { isNotNil } from "@utils/assert";
import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface UseHealthcareTeamDialogProps {
	onError: (error: unknown) => void;
}

export const useHealthcareTeamAcceptDialog = ({ onError }: UseHealthcareTeamDialogProps): void => {
	const dialogContext = useContext(DialogContext);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [hasBeenPresented, setHasBeenPresented] = useState(false);
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);

	const goToPatientDetailsWithErrorAlreadyAccepted = useCallback(
		(patientId: string) => {
			showToast(
				format("patientDetails.healthcareTeam.toastAlreadyJoinedError.title"),
				format("patientDetails.healthcareTeam.toastAlreadyJoinedError.subtitle"),
				"success",
			);
			navigate(RoutesPath.PatientDetails, {
				route: { patientId },
				query: { active: TabPatientDetails.HEALTHCARE_TAB },
			});
		},
		[navigate, format, showToast],
	);

	useEffect(() => {
		const handleHealthcareTeamAcceptDialog = async (patientId: string) => {
			try {
				const healthcareTeamOutput = await healthcareTeamManager.getInvitationDetails(patientId);
				dialogContext?.openModal(ModalId.HealthcareTeamAccept, {
					healthcareTeamOutput,
				});
				sendAnalyticsEventOpenInvitation(healthcareTeamOutput.patient);
			} catch (error) {
				if (error instanceof InvitationAlreadyAcceptedError) {
					goToPatientDetailsWithErrorAlreadyAccepted(patientId);
				}
				onError(error);
				console.error("Failed to send healthcare team invite confirmation", error);
			}
		};
		if (!hasBeenPresented) {
			const patientId = searchParams.get("patientId");
			if (isNotNil(patientId)) {
				handleHealthcareTeamAcceptDialog(patientId);
				setHasBeenPresented(true);
			}
		}
	}, [dialogContext, goToPatientDetailsWithErrorAlreadyAccepted, hasBeenPresented, navigate, onError, searchParams]);
};
